import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, FileText, Download, Globe, Headphones, BookOpen, ArrowRight, MapPin, Clock, Mail, Sparkles } from "lucide-react";
import { api, API_BASE } from "../lib/api";
import SermonCard from "../components/SermonCard";
import { toast } from "sonner";

const SERIES_COLORS = [
  { bg: "#1E5244", ink: "#FFFFFF", eyebrow: "#F5B841" },
  { bg: "#8A4B1F", ink: "#FFFFFF", eyebrow: "#F5B841" },
  { bg: "#2E4A86", ink: "#FFFFFF", eyebrow: "#F5B841" },
];

function Reveal({ children, delay = 0 }) {
  return <div className="reveal" style={{ animationDelay: `${delay}s` }}>{children}</div>;
}

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [recent, setRecent] = useState([]);
  const [series, setSeries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState("");
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/sermons/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/sermons").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
    api.get("/sermons/series").then((r) => setSeries(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const subscribeEmail = async () => {
    const v = email.trim();
    if (!v || !v.includes("@")) return toast.error("Ingresá un correo válido");
    try {
      await api.post("/subscribers", { kind: "email", value: v });
      toast.success("¡Listo! Te vamos a avisar cada nueva prédica.");
      setEmail("");
    } catch { toast.error("No pudimos registrarte ahora."); }
  };

  const playFeatured = () => {
    if (!featured?.audio_url) { toast.error("Esta prédica aún no tiene audio cargado."); return; }
    audioRef.current?.toggle();
  };

  useEffect(() => {
    if (!featured?.audio_url || audioRef.current) return;
    const a = new Audio(featured.audio_url.startsWith("http") ? featured.audio_url : `${process.env.REACT_APP_BACKEND_URL}${featured.audio_url}`);
    a.onplay = () => setPlaying(true);
    a.onpause = () => setPlaying(false);
    a.onended = () => setPlaying(false);
    audioRef.current = {
      toggle: () => (a.paused ? a.play() : a.pause()),
      stop: () => { a.pause(); a.currentTime = 0; },
    };
    return () => { a.pause(); audioRef.current = null; };
  }, [featured?.audio_url]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="eyebrow mb-5 flex items-center gap-2">
                <span className="dot-pulse" /> En vivo cada domingo · CDE
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="hero-headline text-[52px] sm:text-[72px] lg:text-[92px] mb-7" data-testid="hero-headline">
                La Palabra de Dios,<br />
                <span className="text-[#A36A00]">donde estés.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-xl md:text-2xl text-[#3F433C] leading-relaxed max-w-2xl mb-9">
                Prédicas del Pastor Osvaldo Paiva desde Ciudad del Este. Escuchalas, leé el resumen, descargalas en PDF o traducilas al idioma de tu familia.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="flex flex-wrap gap-3">
                <Link to={featured ? `/predicas/${featured.id}` : "/predicas"} className="pill pill-primary" data-testid="btn-hero-listen">
                  <Play size={20} fill="currentColor" /> Escuchar la última
                </Link>
                <Link to="/predicas" className="pill pill-outline" data-testid="btn-hero-all">
                  Ver todas <ArrowRight size={20} />
                </Link>
              </div>
            </Reveal>
          </div>

          {/* NOW PLAYING CARD */}
          <div className="lg:col-span-5">
            <Reveal delay={0.3}>
              <div className="p-card p-6 md:p-8 relative overflow-hidden" data-testid="now-playing-card">
                <div className="flex items-center gap-3 mb-5">
                  <span className={`wave ${playing ? "" : "paused"}`} aria-hidden="true">
                    {Array.from({ length: 12 }).map((_, i) => <span key={i} />)}
                  </span>
                  <span className="eyebrow">Escuchando ahora</span>
                </div>
                {featured ? (
                  <>
                    {featured.series && <div className="badge-soft mb-3" data-testid="now-series">{featured.series}</div>}
                    <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#1B1C1A] mb-2" data-testid="featured-title">{featured.title}</h2>
                    {featured.bible_reference && <div className="font-mono text-base text-[#A36A00] font-semibold mb-4">{featured.bible_reference}</div>}
                    <div className="flex items-center gap-4 mt-6">
                      <button
                        onClick={playFeatured}
                        className="w-20 h-20 rounded-full bg-[#F5B841] text-[#1B1308] flex items-center justify-center shadow-[0_12px_32px_-6px_rgba(245,184,65,0.65)] hover:scale-105 transition border-2 border-[#E6A82A]"
                        aria-label={playing ? "Pausar" : "Reproducir"}
                        data-testid="btn-now-play"
                      >
                        {playing ? <span className="text-3xl font-bold">❚❚</span> : <Play size={32} fill="currentColor" />}
                      </button>
                      <div className="flex-1 flex flex-wrap gap-2">
                        <a href={`${API_BASE}/sermons/${featured.id}/pdf`} target="_blank" rel="noreferrer" className="pill pill-sm pill-outline" data-testid="btn-now-pdf">
                          <FileText size={16} /> PDF
                        </a>
                        <Link to={`/predicas/${featured.id}`} className="pill pill-sm pill-outline" data-testid="btn-now-summary">
                          <BookOpen size={16} /> Resumen
                        </Link>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-6 text-[#55594F] text-base">Pronto publicaremos la primera prédica. Suscribite abajo para recibirla.</div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BENTO */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <Reveal>
          <div className="grid md:grid-cols-4 gap-4 md:gap-5">
            <Link to={featured ? `/predicas/${featured.id}` : "/predicas"} className="p-card p-7 md:p-8 flex flex-col justify-between min-h-[220px] group hover:-translate-y-1 transition" data-testid="bento-escucha">
              <Headphones size={32} className="text-[#1B1C1A]" />
              <div>
                <div className="font-display text-3xl md:text-4xl font-bold leading-none mb-2 text-[#1B1C1A]">Escuchá</div>
                <p className="text-base text-[#3F433C]">la última prédica, mientras manejás o cocinás.</p>
              </div>
              <ArrowRight className="text-[#A36A00] opacity-0 group-hover:opacity-100 transition" />
            </Link>
            <Link to="/predicas" className="p-card-gold p-7 md:p-8 flex flex-col justify-between min-h-[220px] group hover:-translate-y-1 transition" data-testid="bento-resumen">
              <BookOpen size={32} />
              <div>
                <div className="font-display text-3xl md:text-4xl font-bold leading-none mb-2">Leé el resumen</div>
                <p className="text-base" style={{ color: "#4A3610" }}>y los puntos clave en 2 minutos.</p>
              </div>
              <ArrowRight className="opacity-0 group-hover:opacity-100 transition" />
            </Link>
            <Link to="/predicas" className="p-card-green p-7 md:p-8 flex flex-col justify-between min-h-[220px] group hover:-translate-y-1 transition" data-testid="bento-descarga">
              <Download size={32} />
              <div>
                <div className="font-display text-3xl md:text-4xl font-bold leading-none mb-2">Descargá</div>
                <p className="text-base" style={{ color: "#1F3A30" }}>PDF o MP3 para compartir en tu grupo.</p>
              </div>
              <ArrowRight className="opacity-0 group-hover:opacity-100 transition" />
            </Link>
            <Link to="/predicas" className="p-card-blue p-7 md:p-8 flex flex-col justify-between min-h-[220px] group hover:-translate-y-1 transition" data-testid="bento-idioma">
              <Globe size={32} />
              <div>
                <div className="font-display text-3xl md:text-4xl font-bold leading-none mb-2">En tu idioma</div>
                <p className="text-base" style={{ color: "#1F2A4F" }}>guaraní, inglés, portugués, coreano, alemán y francés.</p>
              </div>
              <ArrowRight className="opacity-0 group-hover:opacity-100 transition" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* SERIES */}
      {series.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="eyebrow mb-2">Series</div>
              <h2 className="section-title text-3xl md:text-5xl">Caminos para crecer juntos</h2>
            </div>
            <Link to="/predicas" className="pill pill-sm pill-outline">Ver biblioteca <ArrowRight size={18} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {series.slice(0, 3).map((name, i) => {
              const c = SERIES_COLORS[i % SERIES_COLORS.length];
              return (
                <Link
                  key={name}
                  to={`/predicas?serie=${encodeURIComponent(name)}`}
                  className="rounded-[28px] p-7 md:p-9 min-h-[220px] flex flex-col justify-between group transition hover:-translate-y-1"
                  style={{ background: c.bg, color: c.ink }}
                  data-testid={`series-card-${i}`}
                >
                  <div className="eyebrow" style={{ color: c.eyebrow }}>Serie {String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <div className="font-display text-3xl md:text-[42px] font-bold leading-[1.02] tracking-tight mb-3" style={{ color: c.ink }}>{name}</div>
                    <div className="flex items-center gap-2 text-base opacity-90 group-hover:opacity-100 transition">
                      Explorar la serie <ArrowRight size={18} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* LATEST */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="eyebrow mb-2">Últimas prédicas</div>
            <h2 className="section-title text-3xl md:text-5xl">Palabra que alimenta</h2>
          </div>
          <Link to="/predicas" className="pill pill-sm pill-outline" data-testid="btn-see-all">Ver todas <ArrowRight size={18} /></Link>
        </div>
        {recent.length > 0 ? (
          <div className="grid gap-4">
            {recent.map((s) => <SermonCard key={s.id} s={s} variant="row" />)}
          </div>
        ) : (
          <div className="p-card p-10 text-center text-[#55594F] text-base">Aún no hay prédicas publicadas. Volvé pronto.</div>
        )}
      </section>

      {/* VERSE BAND */}
      {settings?.verse_of_week && (
        <section className="verse-band mb-20" data-testid="verse-of-week-band">
          <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
            <div className="eyebrow mb-5" style={{ color: "#4A3610" }}>Versículo de la semana</div>
            <blockquote className="font-display text-[36px] sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight text-[#1B1308]">
              “{settings.verse_of_week}”
            </blockquote>
            {settings.verse_of_week_ref && (
              <div className="mt-6 font-mono text-base md:text-lg text-[#4A3610]" data-testid="verse-of-week-ref">— {settings.verse_of_week_ref}</div>
            )}
          </div>
        </section>
      )}

      {/* VISIT + SUBSCRIBE (email + WhatsApp Channel) */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="p-card p-7 md:p-10" data-testid="visit-card">
            <div className="eyebrow mb-3">Te esperamos</div>
            <h2 className="section-title text-3xl md:text-5xl mb-7">Este domingo en Ciudad del Este</h2>
            <div className="flex items-start gap-3 mb-5">
              <MapPin size={24} className="text-[#A36A00] mt-1 shrink-0" />
              <div>
                <div className="font-semibold text-[#1B1C1A] text-lg mb-1">Nuestra dirección</div>
                <div className="text-[#3F433C] text-base md:text-lg">{settings?.address || "La dirección se publicará pronto."}</div>
              </div>
            </div>
            <div className="flex items-start gap-3 mb-8">
              <Clock size={24} className="text-[#A36A00] mt-1 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-[#1B1C1A] text-lg mb-2">Horarios</div>
                <div className="flex flex-wrap gap-2">
                  {(settings?.service_hours || "").split("\n").filter(Boolean).map((h, i) => (
                    <span key={i} className="badge-soft text-sm md:text-base">{h}</span>
                  ))}
                  {!settings?.service_hours && <span className="text-[#55594F]">Por confirmar.</span>}
                </div>
              </div>
            </div>
            <a href={settings?.maps_url || "https://share.google/6s7fUwqi81sitv2UO"} target="_blank" rel="noreferrer" className="pill pill-gold" data-testid="btn-visit-maps">
              Cómo llegar <ArrowRight size={20} />
            </a>
          </div>

          {/* Email subscription + WhatsApp Channel */}
          <div className="flex flex-col gap-6">
            <div className="p-card p-7 md:p-10 flex flex-col justify-between" data-testid="email-sub-card" style={{ background: "linear-gradient(140deg, #FFFFFF 0%, #F5EFE2 100%)" }}>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Mail size={24} className="text-[#1E5244]" />
                  <div className="eyebrow" style={{ color: "#1E5244" }}>Correo</div>
                </div>
                <h2 className="section-title text-2xl md:text-4xl mb-3">Recibí cada prédica en tu correo</h2>
                <p className="text-[#3F433C] text-lg mb-6 leading-relaxed">
                  Dejá tu email y te mandamos cada nueva prédica con el audio, el resumen y el PDF.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="input-dark"
                  data-testid="input-email-home"
                />
                <button onClick={subscribeEmail} className="pill pill-primary whitespace-nowrap" data-testid="btn-email-home">
                  <Sparkles size={18} /> Suscribirme
                </button>
              </div>
              <p className="text-sm text-[#55594F] mt-4">Podés darte de baja cuando quieras desde cualquier correo.</p>
            </div>

            {settings?.whatsapp_channel_url && (
              <div className="rounded-[28px] p-7 md:p-9" data-testid="whatsapp-channel-card" style={{ background: "#1E5244", color: "#FFFFFF" }}>
                <div className="eyebrow mb-3" style={{ color: "#F5B841" }}>Canal de WhatsApp</div>
                <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight leading-tight mb-3">Seguinos en WhatsApp</h2>
                <p className="text-lg leading-relaxed mb-6" style={{ color: "#D7E5DF" }}>
                  Cada domingo, la prédica nueva directo en tu celular. Gratis.
                </p>
                <a
                  href={settings.whatsapp_channel_url}
                  target="_blank"
                  rel="noreferrer"
                  className="pill pill-gold"
                  data-testid="btn-whatsapp-channel"
                >
                  Unirme al canal <ArrowRight size={20} />
                </a>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
